import { faker } from '@faker-js/faker'
import slugify from 'slugify'
import { seedPlayers, SeedPlayer, Player } from '../assets/data'

const STORAGE_KEY = 'rock-reference:roster'

// Build a fully-populated Player from a seed entry or a freshly generated one,
// filling in any missing stats and the detail fields that used to be faked on
// every render. Generated once so the data is stable across reloads.
function buildPlayer(base: { name: string; image: string; stats: SeedPlayer['stats'] }): Player {
  return {
    slug: slugify(base.name, { lower: true }),
    name: base.name,
    image: base.image,
    stats: {
      wins: base.stats.wins ?? faker.number.int({ max: 10000 }),
      losses: base.stats.losses ?? faker.number.int({ max: 10000 })
    },
    birthdate: faker.date.birthdate().toDateString(),
    city: faker.location.city(),
    country: faker.location.country(),
    bio: faker.person.bio()
  }
}

// randomuser.me serves its male portraits from a fixed pool at
// men/0.jpg ... men/99.jpg. Asking the API for random results draws from this
// pool *with replacement* and also returns the same face under different
// indices, so URL-based dedup can't prevent repeated faces. Instead we address
// each index exactly once, which uses every distinct photo the pool has and
// never repeats an index.
const PORTRAIT_POOL_SIZE = 100

function portraitUrl(index: number): string {
  return `https://randomuser.me/api/portraits/men/${index}.jpg`
}

function generateRoster(): Player[] {
  const generated: Player[] = seedPlayers.map(buildPlayer)

  for (let index = 0; index < PORTRAIT_POOL_SIZE; index++) {
    generated.push(
      buildPlayer({
        image: portraitUrl(index),
        name: faker.person.fullName({ sex: 'male' }),
        stats: {}
      })
    )
  }

  // Shuffle once, at generation time, so order is stable once persisted.
  return generated.sort(() => Math.random() - 0.5)
}

// Returns the persisted roster, generating and saving it on first use.
export async function loadRoster(): Promise<Player[]> {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored) {
    try {
      return JSON.parse(stored) as Player[]
    } catch {
      // Corrupt data — fall through and regenerate.
    }
  }

  const roster = generateRoster()
  localStorage.setItem(STORAGE_KEY, JSON.stringify(roster))
  return roster
}
